import { Injectable } from '@angular/core';
import { IHint } from './IHint';
import { IModel } from './IModel';
import { IArticle } from './IArticle';
import { SpecialColorSelection } from 'app/shared/SpecialColorSelection';

export type ImageKind = 'perspective' | 'front' | 'scaledrawing';
export type ImageSize = 'large' | 'medium' | 'small' | 'tiny' | 'thumbnail';

/**
 * Provides absolute or relative urls to dynamic assets.
 * An asset is considered dynamic if it's not resolved and bundled by webpack during a build.
 */
@Injectable()
export class PathService {
    private static readonly AssetsPath = "assets";
    private static readonly DataSourcesPath = "assets/data";
    private static readonly ProductImagesPath = "assets/product-images";
    private static readonly Article3DModelsPath = "assets/3d-models";
    private static readonly ArticleVideosPath = "assets/product-videos";
    private static readonly HintImagesPath = "assets/hint-images";
    private static readonly EssentialDataSourceNameFormat = "db.{0}.essential.json"; // language id e.g. "de"
    private static readonly DetailDataSourceNameFormat = "db.{0}.detail.json"; // language id e.g. "de"
    // {0} = model number e.g. "8333.1",
    // {1} = article material number e.g. 754444,
    // {2} = special color e.g. 'r1003' or 'm4'
    // {3} = size e.g. '' or '-medium'
    private static readonly SpecialArticleNameFormat = "{0}/fvm{1}{2}{3}.png";

    public dataPath(languageId: string, essentialData: boolean): string {
        let fileNameFormat = PathService.DetailDataSourceNameFormat;
        if (essentialData)
            fileNameFormat = PathService.EssentialDataSourceNameFormat;

        const fileName = PathService.StringFormat(fileNameFormat, languageId);
        const path = `${PathService.DataSourcesPath}/${fileName}`;
        return path;
    }

    public modelImagePath(model: IModel, imageKind: ImageKind, imageSize: ImageSize): string {
        let path: string;
        if (imageKind === 'perspective')
            path = model["pp-image-url"];
        else if (imageKind === 'front')
            path = model["fv-image-url"];
        else if (imageKind === 'scaledrawing')
            path = model["sd-image-url"];

        path = PathService.pathWithImageSize(path, imageSize);

        if (!path)
        {
            console.error(`${imageKind} path for model ${model.key} was expected but is not present in the data source.`);
            return null;
        }

        return `${PathService.ProductImagesPath}/${path}`;
    }

    /**
     * Returns the asset path to a 3d model json file.
     */
    public article3DModelPath(articleId: string, specialColor?: SpecialColorSelection): string {
        if (!specialColor)
            return `${PathService.Article3DModelsPath}/${articleId}.json`;
        else if (specialColor.isRalColor) {
            // the RAL article has its own id, but we must distinguish between glossy or non glossy
            const nameSuffix = specialColor.isRalMatt ? '' : '-g';
            return `${PathService.Article3DModelsPath}/${articleId}${nameSuffix}.json`;
        } else {
            // the metal article has its own id, but the metal color id differs
            return `${PathService.Article3DModelsPath}/${articleId}-m${specialColor.colorId}.json`;
        }
    }

    /**
     * Returns the asset path to a video showcasing for the given article.
     */
    public articleVideoPath(articleId: string, specialColor?: SpecialColorSelection): string {
        return `${PathService.ArticleVideosPath}/${articleId}${specialColor ? '-' + specialColor.stringRepresentation : ''}.mp4`;
    }

    public imageSizeByZoomFactor(zoomFactor: number): ImageSize {
        if (window.devicePixelRatio === 1)
            return zoomFactor === 1 ? 'tiny' : 'small';
        else
            return 'small';
    }

    public articleImagePath(model: IModel, article: IArticle, imageKind: ImageKind, imageSize: ImageSize, specialColor?: string): string {
        // NOTE: use this code to support both, the regular model image and the actual product image in its special color
        //       when those product image are available in the future...
        // const isSpecialColorArticle = article.attributes["special-type"] !== null && article.attributes["special-type"] !== undefined;
        // const isGoldArticle = isSpecialColorArticle && article.attributes["special-type"][0] === 2;

        // NOTE: for now, fall back to the model image only because special color images are currently N/A
        const isSpecialColorArticle = false;
        const isGoldArticle = false;

        let path;

        if (isSpecialColorArticle && !isGoldArticle && specialColor) {
            const articleKey = article.key.substring(1); // remove the prefixing _
            let imageSizeRaw = '-' + imageSize as string;
            if (imageSize === 'large')
                imageSizeRaw = '';

            let specialColorRaw = '-' + specialColor;
            if (specialColor === undefined)
                specialColorRaw = '';

            path = PathService.StringFormat(PathService.SpecialArticleNameFormat, model["model-number"], articleKey, specialColorRaw, imageSizeRaw);
        } else {
            if (imageKind === 'perspective')
                path = article["pp-image-url"];
            else if (imageKind === 'front')
                path = article["fv-image-url"];
            else if (imageKind === 'scaledrawing')
                path = article["sd-image-url"];

            if (!path) {
                console.error(`${imageKind} path for article ${article.key} was expected but is not present in the data source.`);
                return;
            }

            path = PathService.pathWithImageSize(path, imageSize);
        }

        return `${PathService.ProductImagesPath}/${path}`;
    }

    public modelAwardImagePath(model: IModel, awardId: string): string {
        return `${PathService.ProductImagesPath}\\${model["model-number"]}\\award${awardId}.png`;
    }

    public hintImagePath(hint: IHint) {
        return `${PathService.HintImagesPath}\\${hint["image-id"]}.jpg`;
    }

    /**
     * Modifies a path / filename so that the given imageSize is regarded.
     */
    private static pathWithImageSize(path: string, imageSize: ImageSize) {
        if (imageSize === 'large') {
            return path;
        } else {
            const fileNameWithoutExt = path.substring(0, path.lastIndexOf('.'));
            const fileNameExtension = path.substring(path.lastIndexOf('.'));
            return `${fileNameWithoutExt}-${imageSize}${fileNameExtension}`;
        }
    }

    private static StringFormat(format: string, ...args: any[]) {
        return format.replace(/{(\d+)}/g, function(match, index: number) {
            return args[index] !== undefined
                ? args[index]
                : match;
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/core/path.service.ts